import {
  AudioMode,
  StoryLength,
  StoryMode,
  MusicalAudioMode,
  VideoMode,
  audioModeOptions,
  createDefaultStorySettings,
  languageOptions,
  moralOptions,
  moodOptions,
  musicalAudioModeOptions,
  readerOptions,
  storyLengthOptions,
  storyModeOptions,
  styleOptions,
  videoModeOptions,
  narratorOptions,
} from "../createShared";
import type { Chapter, Sequence, Stage } from "./studioModel";

export type StudioChoices = {
  storyMode: StoryMode;
  reader: string;
  storyLength: StoryLength;
  storyLanguage: string;
  moral: string;
  storyStyle: string;
  narrator: string;
  narratorName: string;
  audioMode: AudioMode;
  includeBackgroundMusic: boolean;
  musicalAudioMode: MusicalAudioMode;
  mood: string;
  characterVoices: Record<
    string,
    { characterName: string; voiceId: string; voiceName: string }
  >;
  videoMode: Exclude<VideoMode, "none"> | "seedance25";
};

export const studioVideoModeOptions = [
  ...videoModeOptions.filter((option) => option.id !== "none"),
];

export const choiceStages = ["blueprint", "world", "audio", "video"] as const;
export type ChoiceStage = (typeof choiceStages)[number];
export const hasStageChoices = (stage: Stage): stage is ChoiceStage =>
  (choiceStages as readonly string[]).includes(stage);

const fields: Record<ChoiceStage, (keyof StudioChoices)[]> = {
  blueprint: [
    "storyMode",
    "reader",
    "storyLength",
    "storyLanguage",
    "moral",
    "narrator",
    "narratorName",
    "mood",
  ],
  world: ["storyStyle"],
  audio: [
    "narrator",
    "narratorName",
    "audioMode",
    "musicalAudioMode",
    "mood",
    "characterVoices",
  ],
  video: ["videoMode"],
};
export const choiceTitles: Record<ChoiceStage, string> = {
  blueprint: "Story choices",
  world: "Visual direction",
  audio: "Voice & sound",
  video: "Video format",
};
const defaults = createDefaultStorySettings({ narrator: "none" });
const oneOf = <T extends string>(
  value: unknown,
  options: readonly T[],
  fallback: T,
): T =>
  typeof value === "string" && options.includes(value as T)
    ? (value as T)
    : fallback;
const text = (value: unknown, fallback: string, max = 160) =>
  typeof value === "string" && value.trim()
    ? value.trim().slice(0, max)
    : fallback;

/** Validate persisted drafts and proposed values against the same catalogs as Classic. */
export function normalizeStudioChoices(
  value: Partial<StudioChoices> = {},
): StudioChoices {
  const storyMode = oneOf(
    value.storyMode,
    storyModeOptions.map((o) => o.id),
    defaults.storyMode,
  );
  const audioMode = oneOf(
    value.audioMode,
    audioModeOptions.map((o) => o.id),
    defaults.audioMode,
  );
  const narrator = text(value.narrator, defaults.narrator);
  const characterVoices: StudioChoices["characterVoices"] = {};
  if (
    storyMode === "Dialogue" &&
    value.characterVoices &&
    typeof value.characterVoices === "object"
  ) {
    Object.entries(value.characterVoices)
      .forEach(([id, voice]) => {
        if (
          !voice ||
          typeof voice !== "object" ||
          !voice.voiceId ||
          !voice.characterName
        )
          return;
        characterVoices[id] = {
          characterName: text(voice.characterName, "Character", 80),
          voiceId: text(voice.voiceId, "none"),
          voiceName: text(voice.voiceName, "Pick for me", 80),
        };
      });
  }
  return {
    storyMode,
    reader: oneOf(value.reader, readerOptions, defaults.reader),
    storyLength: oneOf(
      value.storyLength,
      storyLengthOptions.map((o) => o.id),
      defaults.storyLength,
    ),
    storyLanguage: oneOf(
      value.storyLanguage,
      languageOptions.map((o) => o.id),
      defaults.storyLanguage,
    ),
    moral: oneOf(
      value.moral,
      moralOptions.map((o) => o.id),
      defaults.moral,
    ),
    storyStyle: oneOf(
      value.storyStyle,
      styleOptions.map((o) => o.id),
      defaults.storyStyle,
    ),
    narrator: storyMode === "Musical" ? "none" : narrator,
    narratorName:
      storyMode === "Musical"
        ? "Pick for me"
        : (narratorOptions.find((o) => o.id === narrator)?.label ??
          text(value.narratorName, "Pick for me", 80)),
    audioMode: storyMode === "Musical" ? "classic" : audioMode,
    includeBackgroundMusic: false,
    musicalAudioMode: oneOf(
      value.musicalAudioMode,
      musicalAudioModeOptions.map((o) => o.id),
      defaults.musicalAudioMode,
    ),
    mood: oneOf(
      value.mood,
      moodOptions.map((o) => o.id),
      defaults.mood || moodOptions[0].id,
    ),
    characterVoices,
    videoMode: oneOf(
      value.videoMode,
      ["regular", "premium", "hollywood", "seedance25"] as const,
      "regular",
    ),
  };
}

export function pickStageChoices(
  stage: Stage,
  choices: Partial<StudioChoices>,
): Partial<StudioChoices> {
  return hasStageChoices(stage)
    ? Object.fromEntries(
        fields[stage]
          .filter((key) => choices[key] !== undefined)
          .map((key) => [key, choices[key]]),
      )
    : {};
}

/** Locked choices carry forward. Audio can refine Blueprint's voice or musical theme. */
export function getStudioChoices(
  chapter: Chapter,
  sequence: Sequence,
  stage: Stage,
): StudioChoices {
  // The harness resolves casting before presenting Audio or quoting generation.
  if (stage === "audio" && sequence.audioChoices) return normalizeStudioChoices(sequence.audioChoices);
  const order: Stage[] = [
    "blueprint",
    "script",
    "audio",
    "world",
    "artwork",
    "video",
    "finish",
  ];
  let values: Partial<StudioChoices> = {};
  for (const step of order.slice(0, order.indexOf(stage) + 1)) {
    const saved = sequence.artifacts[step]?.choices;
    const own =
      step === stage && !sequence.locked.includes(step)
        ? (chapter.stageChoices?.[step] ?? saved)
        : saved;
    if (own) values = { ...values, ...pickStageChoices(step, own) };
  }
  const choices = normalizeStudioChoices(values);
  if (
    choices.storyMode === "Dialogue" &&
    order.indexOf(stage) >= order.indexOf("audio")
  ) {
    getStudioCast(sequence).forEach((character) => {
      choices.characterVoices[character.id] ??= {
        characterName: character.name,
        voiceId: "none",
        voiceName: "Pick for me",
      };
    });
  }
  return choices;
}

const label = (options: readonly { id: string; label: string }[], id: string) =>
  options.find((o) => o.id === id)?.label ?? id;

export function studioChoiceRows(
  stage: Stage,
  choices: StudioChoices,
): [string, string][] {
  if (stage === "blueprint")
    return [
      ["Story type", label(storyModeOptions, choices.storyMode)],
      ...(choices.storyMode !== "Musical"
        ? [["Narrator", choices.narratorName] as [string, string]]
        : [["Musical theme", label(moodOptions, choices.mood)] as [string, string]]),
      ["Reader", choices.reader],
      [
        "Length",
        `${label(storyLengthOptions, choices.storyLength)} · ${storyLengthOptions.find((o) => o.id === choices.storyLength)?.detail ?? ""}`,
      ],
      ["Language", label(languageOptions, choices.storyLanguage)],
      ["Moral", label(moralOptions, choices.moral)],
    ];
  if (stage === "world")
    return [["Visual style", label(styleOptions, choices.storyStyle)]];
  if (stage === "audio")
    return choices.storyMode === "Musical"
      ? [
          ["Performance", "Musical"],
          ["Audio", label(musicalAudioModeOptions, choices.musicalAudioMode)],
          ["Musical theme", label(moodOptions, choices.mood)],
        ]
      : [
          ["Narrator", choices.narratorName],
          ["Experience", label(audioModeOptions, choices.audioMode)],
          [
            "Music",
            choices.audioMode === "cinematic"
              ? "Original score"
              : "None",
          ],
          ...(choices.storyMode === "Dialogue"
            ? Object.values(choices.characterVoices).map(
                (v) => [v.characterName, v.voiceName] as [string, string],
              )
            : []),
        ];
  if (stage === "video")
    return [["Format", choices.videoMode === "seedance25" ? "Seedance 2.5 (previous selection)" : label(studioVideoModeOptions, choices.videoMode)]];
  return [];
}

export const describeStudioChoices = (stage: Stage, choices: StudioChoices) =>
  studioChoiceRows(stage, choices)
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");

export const getStudioCast = (sequence: Sequence) =>
  sequence.artifacts.script?.characters ??
  sequence.artifacts.world?.characters ??
  sequence.artifacts.blueprint?.characters ??
  [];
